import React from 'react';
import { bool, func, object } from 'prop-types';
import { Button } from 'antd-v5';

import Errors from './Errors';
import { isValidDate } from './Field/props';

// One @, something either side, a dot in the domain, no whitespace.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const normaliseEmail = value => `${value || ''}`.trim().toLowerCase();

export const FIELD_STYLES = {
  width: 506,
  maxWidth: '100%',
  minWidth: 'auto',
};
export const HEADING_CLASSES = 'fs-16 fw-600 m-top-0 m-btm-8';

const Wizard = ({
  data,
  setData,
  onSubmit,
  showAll,
  isSola,
  steps,
  handleClick,
  activeStepIndex,
  previous,
  next,
  handleStepChange,
  wizardForm,
  getFields,
  updateValues,
  disableContinue,
  errors,
  setErrors,
  bankAccountStores,
}) => {
  // helper to show step-level errors
  const pushError = error => {
    setErrors(errorList => [...errorList, error].filter((value, index, self) => self.indexOf(value) === index));
  };

  const validateField = field => {
    const name = field.label || field.id;
    const value = `${field.value || ''}`;
    const isNumeric = field.inputMode === 'numeric';
    const unit = isNumeric ? 'digits' : 'characters';

    if (Boolean(field.optional) && !value) {
      return false;
    }

    if (!value) {
      pushError(`${name} is required`);
      return true;
    }

    // Digits only: Number() would accept '1e5' or ' 12 ' for an account number.
    if (isNumeric && !/^\d+$/.test(value)) {
      pushError(`${name} must contain only digits`);
      return true;
    }

    // The blur validator (validateDateField) only colours the input; this is what stops the
    // submit. Same rule, so the two never disagree.
    if (field.format === 'date' && !isValidDate(value)) {
      pushError(`${name} must be a valid date in MM/DD/YYYY format`);
      return true;
    }

    if (field.type === 'email' && !EMAIL_PATTERN.test(value.trim())) {
      pushError(`${name} must be a valid email address`);
      return true;
    }

    const minLength = Number(field.minLength);
    const maxLength = Number(field.maxLength);

    if (field.minLength && !Number.isNaN(minLength) && value.length < minLength) {
      pushError(
        field.maxLength && minLength !== maxLength
          ? `${name} must be between ${minLength} and ${maxLength} ${unit}`
          : `${name} must be ${minLength} ${unit}`,
      );
      return true;
    }

    if (field.maxLength && !Number.isNaN(maxLength) && value.length > maxLength) {
      pushError(`${name} must be no more than ${maxLength} ${unit}`);
      return true;
    }

    return false;
  };

  // helper to validate active fields
  const validateFields = activeFields => {
    const errorFields = activeFields.filter(el => {
      return validateField(el);
    });

    // Cross-field: the confirmation must match the email it confirms. Both only exist on the
    // editable Sola step; anywhere else one is absent and this is skipped.
    const email = activeFields.find(({ id }) => id === 'businessEmail');
    const confirm = activeFields.find(({ id }) => id === 'businessEmailConfirm');

    if (
      email &&
      confirm &&
      email.value &&
      confirm.value &&
      normaliseEmail(email.value) !== normaliseEmail(confirm.value)
    ) {
      pushError('Business Email and Confirm Business Email must match');

      if (!errorFields.includes(confirm)) {
        errorFields.push(confirm);
      }
    }

    return errorFields;
  };

  // form submission
  const isSubmitButton = showAll || activeStepIndex === 1;

  // get active fields, for validation & updating data
  const getActiveFields = () => {
    const fields = getFields();
    const activeFields = fields.filter(({ active }) => active);

    return activeFields;
  };

  // main handler for next/submit
  const handleSubmit = evt => {
    evt.preventDefault();

    handleStepChange();

    // validation
    const activeFields = getActiveFields();
    const errorFields = validateFields(activeFields);
    const isValid = !errorFields.length;

    // update our main `data` object with errors
    setData(d =>
      errorFields.reduce(
        (obj, { id }) => ({
          ...obj,
          [id]: { ...d[id], hasError: true },
        }),
        d,
      ),
    );

    // don't submit if invalid
    if (!isValid) {
      return;
    }

    if (isSubmitButton) {
      onSubmit(data, evt.target);
    } else if (activeStepIndex === 2) {
      handleClick('', false);
      handleStepChange();
    } else next();
  };

  return (
    <>
      <Errors errors={errors} />

      <form
        id="wizard-form-multi"
        onSubmit={handleSubmit}
        // The browser's own validation would otherwise pre-empt ours: minLength/maxLength (which
        // getFields reads from the DOM) make it show its own tooltip and never fire onSubmit, so
        // our messages (by label, in "digits") never appear -- and inactive steps are display:none,
        // which the browser cannot tooltip at all.
        noValidate
        ref={wizardForm}
        style={{ marginBottom: '150px', position: 'relative' }}
      >
        {Object.keys(steps).map((key, index) => {
          const { crumb, title, Component } = steps[key];
          const active = showAll || activeStepIndex === index;
          return (
            <section
              className="pos-rel"
              key={crumb}
              /*
               * BlueSnap javascript requires that all inputs are found in the DOM,
               * so we hide them instead
               */
              style={{
                ...FIELD_STYLES,
                display: active ? 'block' : 'none',
              }}
            >
              <h2 className={HEADING_CLASSES}>{title}</h2>
              <Component
                handleClick={handleClick}
                steps={Object.keys(steps)}
                data={data}
                setData={setData}
                updateValues={updateValues}
                active={active}
                // Steps render Sola-only fields from this.
                isSola={isSola}
                setDisableContinue={disabled => (active ? setDisableContinue(disabled) : null)}
                bankAccountStores={bankAccountStores}
              />
            </section>
          );
        })}

        <div className="buttons flex-row-center pos-abs m-top-50" style={{ ...FIELD_STYLES, gap: '12px' }}>
          {activeStepIndex > 0 && (
            <Button
              size="large"
              onClick={() => {
                handleStepChange();
                previous();
                if (activeStepIndex === 2) {
                  handleClick('', false);
                }
              }}
            >
              Go Back
            </Button>
          )}
          <Button type="primary" size="large" htmlType="submit" disabled={disableContinue}>
            {isSubmitButton ? 'Complete' : 'Continue'}
          </Button>
        </div>
      </form>
    </>
  );
};

Wizard.propTypes = {
  initialValues: object,
  onSubmit: func,
  showAll: bool,
  steps: object,
};

Wizard.defaultProps = {
  initialValues: {},
};
export default Wizard;
